/**
 * fire_sim.ts — the 'sim' rung (fire realism R1, 2026-09-25): ONE shared GPU fluid grid for the hero fires on
 * ultra + WebGPU. A 1/30-scale port of three r185's own `webgpu_volume_fire` example (PR #33848): the same
 * passes — curl noise precomputed once; advect velocity with buoyancy (hot rises, smoke weighs), a thermal curl
 * turbulence that decays with age and a weak ambient one, damping, a soft boundary; divergence; Jacobi pressure
 * (4 iterations, ping-pong); project; advect the dye (R = smoke density, G = temperature, B = age) with
 * dissipation and cooling — on a 32 × 64 × 32 grid of RGBA16F Storage3DTextures (~4 MB of fields, not the
 * example's ~128 MB), stepped at 30 Hz (one step per ~2 frames at 60 fps). The example's teapot emitter is a
 * fire BED here: a disc at the grid's floor injecting density and heat, breathing with the hero fire's flicker,
 * and the weather wind leans the column.
 *
 * Every hero fire (the nearest two within 30 m) samples the SAME grid through its own box, rotated and mirrored
 * by its seed so two fires side by side never look like twins; the march, the blackbody LUT, the soot, the
 * premultiplied blend and the depth clamp are the 'volume' rung's (lib/vfx/fire_volume.ts `simVolumeMaterial`).
 * WebGPU ONLY — `fireRungFor` never asks for it on WebGL 2 (compute does not exist there).
 */
import * as THREE from 'three/webgpu';
import {
  Fn, vec3, vec4, uvec3, float, uniform, texture3D, textureStore, instanceIndex, smoothstep, max, min, floor, If, length, select,
} from 'three/tsl';
// @ts-expect-error — the r185 addon ships no typings (the example's own curl-noise helper).
import { snoiseVec3 } from 'three/addons/tsl/math/curlNoise.js';

/* eslint-disable @typescript-eslint/no-explicit-any */
type N = any;

export const SIM_GX = 32, SIM_GY = 64, SIM_GZ = 32;
const CELLS = SIM_GX * SIM_GY * SIM_GZ;
export const SIM_HZ = 30;
const JACOBI = 4;
/** The grid's world size at fire size 1 (the campfire box). */
const WX = 1.3, WY = 2.3, WZ = 1.3;

function storage3D(name: string): THREE.Storage3DTexture {
  const t = new THREE.Storage3DTexture(SIM_GX, SIM_GY, SIM_GZ);
  t.name = name;
  t.format = THREE.RGBAFormat;
  t.type = THREE.HalfFloatType; // rgba16float: storage-writable AND linearly filterable
  t.minFilter = THREE.LinearFilter; t.magFilter = THREE.LinearFilter;
  t.wrapS = t.wrapT = t.wrapR = THREE.ClampToEdgeWrapping;
  return t;
}

export class FireSim {
  readonly dyeRead: N;               // the render side's texture node (its value follows the ping-pong)
  private readonly dyeA: THREE.Storage3DTexture;
  private readonly dyeB: THREE.Storage3DTexture;
  private readonly dyeNode: N;
  private readonly passes: { curl: N; advectVel: N; div: N; jAB: N; jBA: N; project: N; advectDye: { ab: N; ba: N } };
  private readonly uDt = uniform(1 / SIM_HZ);
  private readonly uTime = uniform(0);
  private readonly uEmit = uniform(1);
  private readonly uWind = uniform(new THREE.Vector3());
  private acc = 0;
  private time = 0;
  private readonly legacy: boolean;
  private curlDone = false;
  steps = 0;
  /** The grid's fields, for the readback bed (`fire_sim_probe.html`) — never written from outside. */
  readonly debug: { vel: THREE.Storage3DTexture; dyeA: THREE.Storage3DTexture; dyeB: THREE.Storage3DTexture };

  /**
   * `legacyFloor` rebuilds the pre-2026-09-29 soft boundary (every face faded, the floor included) — ONLY for the
   * readback bed's before/after (`fire_sim_probe.html`); the game never passes it.
   */
  constructor(private readonly renderer: { compute(n: unknown): void }, opts: { legacyFloor?: boolean } = {}) {
    this.legacy = opts.legacyFloor === true;
    const velA = storage3D('fire-sim-vel-a'), velB = storage3D('fire-sim-vel-b');
    this.dyeA = storage3D('fire-sim-dye-a'); this.dyeB = storage3D('fire-sim-dye-b');
    const div = storage3D('fire-sim-div'), pA = storage3D('fire-sim-press-a'), pB = storage3D('fire-sim-press-b');
    const curl = storage3D('fire-sim-curl');
    this.debug = { vel: velA, dyeA: this.dyeA, dyeB: this.dyeB };
    curl.wrapS = curl.wrapT = curl.wrapR = THREE.RepeatWrapping;
    this.dyeNode = texture3D(this.dyeA); // the velocity pass's read of the CURRENT dye (its value follows the ping-pong)
    this.dyeRead = texture3D(this.dyeA);
    const curlNode: N = texture3D(curl);
    const size = vec3(WX, WY, WZ);
    const TX = 1 / SIM_GX, TY = 1 / SIM_GY, TZ = 1 / SIM_GZ;
    const coordOf = (id: N): N => uvec3(id.mod(SIM_GX), id.div(SIM_GX).mod(SIM_GY), id.div(SIM_GX * SIM_GY));
    const uvwOf = (c: N): N => vec3(c).add(0.5).div(vec3(SIM_GX, SIM_GY, SIM_GZ));
    const uDt = this.uDt, uTime = this.uTime, uWind = this.uWind;
    const dyeNode: N = this.dyeNode;

    // 0) Curl noise, once (the example's, on this grid's aspect).
    const curlPass = Fn(() => {
      const c = coordOf(instanceIndex), uvw = uvwOf(c);
      const freq = float(6), e = float(0.1).div(freq);
      const p = uvw.mul(vec3(WX / WY, 1, WZ / WY));
      const dx = vec3(e, 0, 0), dy = vec3(0, e, 0), dz = vec3(0, 0, e);
      const x0 = snoiseVec3(p.sub(dx).mul(freq)), x1 = snoiseVec3(p.add(dx).mul(freq));
      const y0 = snoiseVec3(p.sub(dy).mul(freq)), y1 = snoiseVec3(p.add(dy).mul(freq));
      const z0 = snoiseVec3(p.sub(dz).mul(freq)), z1 = snoiseVec3(p.add(dz).mul(freq));
      const cx = y1.z.sub(y0.z).sub(z1.y).add(z0.y), cy = z1.x.sub(z0.x).sub(x1.z).add(x0.z), cz = x1.y.sub(x0.y).sub(y1.x).add(y0.x);
      textureStore(curl, c, vec4(vec3(cx, cy, cz).mul(5), 0)).toWriteOnly();
    })().compute(CELLS).setName('fireSimCurl');

    // 1) Advect velocity + buoyancy, weight, turbulence, damping, the wind, a soft boundary.
    const advectVel = Fn(() => {
      const c = coordOf(instanceIndex), uvw = uvwOf(c);
      const vel = texture3D(velA, uvw, 0).xyz;
      const prev = uvw.sub(vel.div(size).mul(uDt));
      const nv = texture3D(velA, prev, 0).xyz.toVar();
      const dye = dyeNode.sample(uvw).level(0);
      const density = dye.r, temp = dye.g, age = dye.b;
      nv.addAssign(vec3(0, temp.mul(2.6).sub(density.mul(0.12)).mul(WY), 0).mul(uDt));
      const tpos = uvw.add(vec3(0, age.negate().mul(0.6), age.mul(0.13)).div(6));
      const thermal = curlNode.sample(tpos).level(0).xyz.mul(2.8).mul(temp).mul(age.mul(-0.12).exp());
      const apos = uvw.mul(0.5).add(vec3(0, uTime.mul(0.25), uTime.mul(0.06)).div(6));
      const ambient = curlNode.sample(apos).level(0).xyz.mul(0.55).mul(density);
      nv.addAssign(thermal.add(ambient).mul(WY).mul(uDt));
      nv.addAssign(uWind.mul(uvw.y).mul(uDt));
      nv.mulAssign(max(float(1).sub(float(0.25).mul(uDt)), 0));
      if (opts.legacyFloor) {
        const edge = min(uvw, vec3(1).sub(uvw));
        nv.mulAssign(smoothstep(0, 0.08, min(edge.x, min(edge.y, edge.z))));
      } else {
        // THE FLOOR IS THE GROUND (shader gaps R5, 2026-09-29 — "a glow with no flame body"): three's example fades the
        // velocity near EVERY face because its emitter (a teapot) floats mid-grid; this port's emitter is a BED on the
        // floor (uvw.y < 0.06), inside that 0.08 fade, so every step multiplied the bed's own updraft by 0.03–0.7 and the
        // heat never left the lowest rows (the readback bed: the heat centroid at 7 % of the height after 5 s). The
        // walls and the ceiling still fade; the floor only refuses a downdraft through the ground.
        // THE CEILING IS OPEN: a closed top stalled the risen gas under it and it spread sideways until the whole box
        // glowed (the readback: the top rows the hottest after 5 s). The top neither fades the velocity nor holds
        // pressure (the Jacobi and project passes read p = 0 above it) — the plume leaves, as a flame's gas does.
        // the walls fade over a narrower band than the example's 0.08: a wide fade stalled the plume's smoke against the
        // sides and drew the box's outline
        const edgeXZ = min(uvw.xz, vec3(1).sub(uvw).xz);
        nv.mulAssign(smoothstep(0, 0.04, min(edgeXZ.x, edgeXZ.y)));
        nv.assign(vec3(nv.x, select(uvw.y.lessThan(1.5 / SIM_GY), max(nv.y, 0), nv.y), nv.z));
      }
      textureStore(velB, c, vec4(nv, 0)).toWriteOnly();
    })().compute(CELLS).setName('fireSimAdvectVel');

    // 2) Divergence.
    const divPass = Fn(() => {
      const c = coordOf(instanceIndex), uvw = uvwOf(c);
      const r = texture3D(velB, uvw.add(vec3(TX, 0, 0)), 0).x, l = texture3D(velB, uvw.sub(vec3(TX, 0, 0)), 0).x;
      const u = texture3D(velB, uvw.add(vec3(0, TY, 0)), 0).y, d = texture3D(velB, uvw.sub(vec3(0, TY, 0)), 0).y;
      const f = texture3D(velB, uvw.add(vec3(0, 0, TZ)), 0).z, b = texture3D(velB, uvw.sub(vec3(0, 0, TZ)), 0).z;
      textureStore(div, c, vec4(r.sub(l).add(u.sub(d)).add(f.sub(b)).mul(0.5), 0, 0, 0)).toWriteOnly();
    })().compute(CELLS).setName('fireSimDivergence');

    // 3) Jacobi pressure (ping-pong).
    // above the top row the pressure is 0 (the open ceiling); the legacy build keeps the clamped (closed) top
    const openTop = (v: N, uvw: N): N => (opts.legacyFloor ? v : select(uvw.y.add(TY).greaterThan(1), float(0), v));
    const jacobi = (rd: THREE.Storage3DTexture, wr: THREE.Storage3DTexture, name: string): N => Fn(() => {
      const c = coordOf(instanceIndex), uvw = uvwOf(c);
      const s = texture3D(rd, uvw.add(vec3(TX, 0, 0)), 0).x.add(texture3D(rd, uvw.sub(vec3(TX, 0, 0)), 0).x)
        .add(openTop(texture3D(rd, uvw.add(vec3(0, TY, 0)), 0).x, uvw)).add(texture3D(rd, uvw.sub(vec3(0, TY, 0)), 0).x)
        .add(texture3D(rd, uvw.add(vec3(0, 0, TZ)), 0).x).add(texture3D(rd, uvw.sub(vec3(0, 0, TZ)), 0).x);
      textureStore(wr, c, vec4(s.sub(texture3D(div, uvw, 0).x).div(6), 0, 0, 0)).toWriteOnly();
    })().compute(CELLS).setName(name);

    // 4) Project.
    const project = Fn(() => {
      const c = coordOf(instanceIndex), uvw = uvwOf(c);
      const g = vec3(
        texture3D(pA, uvw.add(vec3(TX, 0, 0)), 0).x.sub(texture3D(pA, uvw.sub(vec3(TX, 0, 0)), 0).x),
        openTop(texture3D(pA, uvw.add(vec3(0, TY, 0)), 0).x, uvw).sub(texture3D(pA, uvw.sub(vec3(0, TY, 0)), 0).x),
        texture3D(pA, uvw.add(vec3(0, 0, TZ)), 0).x.sub(texture3D(pA, uvw.sub(vec3(0, 0, TZ)), 0).x),
      ).mul(0.5);
      textureStore(velA, c, vec4(texture3D(velB, uvw, 0).xyz.sub(g), 0)).toWriteOnly();
    })().compute(CELLS).setName('fireSimProject');

    // 5) Advect the dye (+ the fire BED) — the ping-pong's two directions, built in `dyePasses`.
    this.passes = { curl: curlPass, advectVel, div: divPass, jAB: jacobi(pA, pB, 'fireSimJacobiAB'), jBA: jacobi(pB, pA, 'fireSimJacobiBA'), project, advectDye: this.dyePasses(uvwOf, coordOf, size, velA, curlNode) };
  }

  /**
   * The two dye passes (A → B, B → A). A compute node binds its storage TARGET at build time, so the ping-pong is
   * two nodes, each reading the other texture through its own texture3D node — never a `.value` swap on a store.
   */
  private dyePasses(uvwOf: (c: N) => N, coordOf: (id: N) => N, size: N, velA: THREE.Storage3DTexture, curlNode: N): { ab: N; ba: N } {
    const make = (read: THREE.Storage3DTexture, write: THREE.Storage3DTexture, name: string): N => {
      const readNode: N = texture3D(read);
      return Fn(() => {
        const c = coordOf(instanceIndex), uvw = uvwOf(c);
        const vel = texture3D(velA, uvw, 0).xyz;
        const prev = uvw.sub(vel.div(size).mul(this.uDt));
        const dye = readNode.sample(prev).level(0);
        // THE FLAME ENDS (2026-09-29): once the gas really rises (THE FLOOR IS THE GROUND) it crosses the 2.3 m box in
        // ~0.8 s; at the example's cooling (1.25/s) it arrived at the top with a third of its heat and the whole box
        // glowed as a pillar. A campfire's visible flame ends around a metre: cool at 4/s (the tip near 0.35 s of rise),
        // and let the smoke thin twice as fast so it does not fill the box.
        const coolK = this.legacy ? 1.25 : 4.0, dissK = this.legacy ? 0.55 : 1.6;
        const density = dye.r.mul(max(float(1).sub(float(dissK).mul(this.uDt)), 0)).toVar();
        const temp = dye.g.mul(max(float(1).sub(float(coolK).mul(this.uDt)), 0)).toVar();
        const dims = vec3(SIM_GX, SIM_GY, SIM_GZ);
        const age = readNode.sample(floor(prev.mul(dims)).add(0.5).div(dims)).level(0).b.add(this.uDt).toVar();
        const r = length(uvw.xz.sub(0.5)).div(0.27);
        const bed = float(1).sub(smoothstep(0.6, 1.0, r)).mul(float(1).sub(smoothstep(0.02, 0.06, uvw.y)));
        const flick = curlNode.sample(vec3(uvw.x.mul(2), this.uTime.mul(0.9), uvw.z.mul(2))).level(0).x.mul(0.25).add(0.75);
        const inject = bed.mul(flick).mul(this.uEmit);
        // a rising flame carries its heat away (the trapped legacy bed simply accumulated it): inject hotter, smokier less
        density.addAssign(inject.mul(this.uDt).mul(this.legacy ? 6 : 4));
        temp.assign(temp.add(inject.mul(this.uDt).mul(this.legacy ? 8.5 : 16)).clamp(0, 12));
        If(density.lessThanEqual(0.01), () => { age.assign(0); });
        textureStore(write, c, vec4(density, temp, age, 1)).toWriteOnly();
      })().compute(CELLS).setName(name);
    };
    return { ab: make(this.dyeA, this.dyeB, `${'fireSimAdvectDye'}AB`), ba: make(this.dyeB, this.dyeA, `${'fireSimAdvectDye'}BA`) };
  }

  /**
   * Step at 30 Hz (never more than one step a frame: a slow frame skips, it never spirals). `emit` = the hero
   * fire's flicker-driven intensity (0 = no hero near: the grid is not stepped at all), wind = m/s in the box.
   */
  update(dt: number, emit: number, windX: number, windZ: number): void {
    if (emit <= 0) { this.acc = 0; return; }
    if (!this.curlDone) { this.renderer.compute(this.passes.curl); this.curlDone = true; }
    this.acc += dt;
    if (this.acc < 1 / SIM_HZ) return;
    this.acc = Math.min(this.acc - 1 / SIM_HZ, 1 / SIM_HZ);
    this.time += 1 / SIM_HZ;
    (this.uTime as unknown as { value: number }).value = this.time;
    (this.uEmit as unknown as { value: number }).value = emit;
    (this.uWind.value as THREE.Vector3).set(windX, 0, windZ);
    const p = this.passes;
    // The dye the velocity pass reads is the CURRENT one (the last written).
    const cur = this.steps % 2 === 0 ? this.dyeA : this.dyeB;
    this.dyeNode.value = cur;
    this.renderer.compute(p.advectVel);
    this.renderer.compute(p.div);
    for (let i = 0; i < JACOBI; i++) this.renderer.compute(i % 2 === 0 ? p.jAB : p.jBA);
    this.renderer.compute(p.project);
    this.renderer.compute(this.steps % 2 === 0 ? p.advectDye.ab : p.advectDye.ba);
    this.steps++;
    // The renderers read the texture just written.
    this.dyeRead.value = this.steps % 2 === 0 ? this.dyeA : this.dyeB;
  }
}
